import { graphHooks } from '@tunasong/graph-lib/react'
import type { Agent, Persisted } from '@tunasong/schemas'
import { isAgent } from '@tunasong/schemas'

/**
 * Live list of agent children of a host entity, kept in sync with the
 * graph cache.
 *
 * Wraps `graphHooks.useEntityChildren` with `filter: 'agent'` and an
 * `isAgent` narrowing so callers get a tightly-typed list. No additional
 * fetches — children for the host are loaded by other rendering paths
 * (block tree, child list); this hook just reads the same cache.
 *
 * Re-renders consumers when:
 *  - `loadChildEntities` invalidates after `receiveEntity` (new
 *    attachment / deletion).
 *  - The graph cache mutates via `receivePartialEntity` (e.g. a
 *    runtime-status broadcast lands on one of the agents).
 *
 * Returns an empty array when `parentId` is undefined or the cache is
 * cold; the consumer (typically the Agent Rail) renders nothing in that
 * state.
 */
export const useAgentChildren = (parentId?: string | null): Persisted<Agent>[] => {
  const { entities } = graphHooks.useEntityChildren({
    parentId: parentId ?? undefined,
    filter: 'agent',
    skip: !parentId,
  })
  return entities.filter(isAgent) as Persisted<Agent>[]
}
